import en from './en.json';

/**
 * Traducción de la interfaz al inglés sin tocar cada pantalla: un diccionario español → inglés
 * que se aplica a los textos que aparecen en la página (y a placeholder, aria-label, title y alt).
 * Las frases con valores ("Llega en {}") se traducen por patrón.
 */
export type Lang = 'es' | 'en';
const KEY = 'tcrd:lang';

export function getLang(): Lang {
  try {
    const v = localStorage.getItem(KEY);
    if (v === 'es' || v === 'en') return v;
  } catch {
    /* sin almacenamiento */
  }
  return typeof navigator !== 'undefined' && /^en\b/i.test(navigator.language) ? 'en' : 'es';
}

export function setLang(lang: Lang) {
  try {
    localStorage.setItem(KEY, lang);
  } catch {
    /* sin almacenamiento */
  }
  // Recarga para volver a pintar toda la interfaz en el idioma elegido.
  window.location.reload();
}

const dict = en as Record<string, string>;
const exact = new Map<string, string>();
const patterns: Array<{ re: RegExp; out: string }> = [];
for (const [es, tr] of Object.entries(dict)) {
  if (!es.includes('{}')) {
    exact.set(es.trim(), tr.trim());
    continue;
  }
  const re = new RegExp('^' + es.trim().split('{}').map((p) => p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('(.+?)') + '$', 's');
  patterns.push({ re, out: tr.trim() });
}
// Las plantillas más largas primero (más específicas).
patterns.sort((a, b) => b.re.source.length - a.re.source.length);

/** Traduce un texto (o lo devuelve igual si no está en el diccionario). */
export function translate(text: string, depth = 0): string {
  const core = text.trim();
  if (!core || depth > 2) return text;
  let out = exact.get(core);
  if (out === undefined) {
    for (const p of patterns) {
      const m = p.re.exec(core);
      if (!m) continue;
      let i = 1;
      out = p.out.replace(/\{\}/g, () => translate(m[i++] ?? '', depth + 1));
      break;
    }
  }
  if (out === undefined) return text;
  // Se conservan los espacios del texto original (separan palabras entre elementos).
  const lead = text.match(/^\s*/)?.[0] ?? '';
  const trail = text.match(/\s*$/)?.[0] ?? '';
  return lead + out + trail;
}

const ATTRS = ['placeholder', 'aria-label', 'title', 'alt'];
const SKIP = new Set(['SCRIPT', 'STYLE', 'TEXTAREA', 'CODE', 'PRE']);
const written = new WeakMap<Node, string>();

/** La app del conductor y la administración quedan en español; el inglés es para los clientes. */
const staffArea = () => /^\/(driver|admin)(\/|$)/.test(window.location.pathname);

function translateNode(node: Node) {
  if (staffArea()) return;
  if (node.nodeType === Node.TEXT_NODE) {
    const parent = node.parentElement;
    if (!parent || SKIP.has(parent.tagName) || parent.closest('[data-no-translate],[contenteditable="true"]')) return;
    // Nombres de personas: nunca se traducen (p. ej. un apellido que coincide con una palabra).
    if (parent.closest('[data-person]')) return;
    const v = node.nodeValue ?? '';
    if (written.get(node) === v) return;
    const t = translate(v);
    if (t !== v) {
      written.set(node, t);
      node.nodeValue = t;
    }
    return;
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return;
  const el = node as Element;
  if (SKIP.has(el.tagName) || el.closest('[data-no-translate]')) return;
  for (const a of ATTRS) {
    const v = el.getAttribute(a);
    if (v) {
      const t = translate(v);
      if (t !== v) el.setAttribute(a, t);
    }
  }
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT);
  let n = walker.nextNode();
  while (n) {
    if (n.nodeType === Node.TEXT_NODE) translateNode(n);
    else {
      const e = n as Element;
      for (const a of ATTRS) {
        const v = e.getAttribute(a);
        if (v) {
          const t = translate(v);
          if (t !== v) e.setAttribute(a, t);
        }
      }
    }
    n = walker.nextNode();
  }
}

/** Activa la traducción automática de la página si el idioma es inglés. */
export function startTranslator() {
  if (typeof document === 'undefined' || getLang() !== 'en') return;
  document.documentElement.lang = 'en';
  translateNode(document.body);
  new MutationObserver((muts) => {
    for (const m of muts) {
      if (m.type === 'characterData') translateNode(m.target);
      else if (m.type === 'attributes') {
        const el = m.target as Element;
        const v = el.getAttribute(m.attributeName!);
        if (v) {
          const t = translate(v);
          if (t !== v) el.setAttribute(m.attributeName!, t);
        }
      } else m.addedNodes.forEach(translateNode);
    }
  }).observe(document.body, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ATTRS });
}

/** Para textos que no pasan por la página (por ejemplo los avisos breves). */
export const tr = (text: string) => (getLang() === 'en' ? translate(text) : text);
